import React, { useEffect, useRef, useState } from 'react';
import './canvas.css';
import { drawText } from 'canvas-txt';
import  Plus from '../../img/btn_plus.svg';
import Minus from '../../img/btn_minus.svg';
import Logo from '../../img/logo.svg';
import Upload from '../../img/btn_up.svg';
import CMJ from '../../img/logo.png';

const zoomFactor = 0.2;
/**  CanvasComponent is responsible for rendering the main canvas and background canvas.
 * It handles drawing text and images onto the canvas, as well as zooming functionality.
    * It uses the `canvas-txt` library to draw text on the canvas.
    * It manages the background image and text objects passed in through props.
    * It provides zoom in and zoom out functionality.
    * It also handles rendering the canvas to an image file when requested.
    * It uses React hooks for state management and side effects.
    * It uses refs to access the canvas elements directly for drawing.
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.background - The URL of the background image to be drawn on the canvas.
 * @param {Object} props.content - The content object containing text objects to be drawn on the canvas.
 * @param {number} props.activeID - The ID of the currently active text object to be highlighted.
 * @param {boolean} props.renderAllTrigger - A trigger to render all text objects and download the canvas as an image.
 * @returns {JSX.Element} The rendered CanvasComponent.
 */
const CanvasComponent = ({ background, content, activeID,  renderAllTrigger }) => {
    // Contexts
    const canvasRef = useRef(null);
    const bgCanvasRef = useRef(null);
    const [draw_ctx, setDraw_ctx] = useState(null);
    const [img_ctx, setImg_ctx] = useState(null);
    const [hovered, setHovered] = useState(false);
    const [zoom, setZoom] = useState(1);

    useEffect(()=>{
        setImg_ctx( bgCanvasRef.current.getContext("2d") );
        setDraw_ctx( canvasRef.current.getContext("2d") );
    }, [canvasRef,bgCanvasRef]);

    // Draw canvas
    const DrawTxt = (ctx, reset=true, renderBounds=true) => {        
        const text_content = content.text_objs;   
        if (!ctx || !text_content) return;
        if (reset) ctx.reset();        
        
        // Draw text
        for ( const [id, obj] of Object.entries(text_content) ){
            if ( renderBounds && id==activeID ){
                ctx.strokeStyle = "yellow";
                ctx.beginPath();
                ctx.rect(obj.x, obj.y, obj.width, obj.height);
                ctx.stroke();
            }
            ctx.fillStyle = obj.txtColor;
            ctx.strokeStyle = obj.strokeColor; 
            ctx.shadowColor = obj.strokeColor;
            ctx.shadowBlur = 5; // Blur radius
            ctx.shadowOffsetX = 0; // Horizontal offset
            ctx.shadowOffsetY = 0; // Vertical offset

            ctx.fillStyle = obj.txtColor;
            const {height} = drawText( ctx, obj.text, {
                font: obj.font || 'Arial',
                x: obj.x, 
                y: obj.y,
                width: obj.width,
                height: obj.height,
                fontSize: obj.fontSize,
                align: obj.align,
                vAlign: obj.vAlign,
                fontWeight: 500
              })            
        }
    }    

    // Image Canvas
    const DrawImage = () => {
        var img = new Image();
        img.onload = ()=>{            
            img_ctx.drawImage(img, 0,0);
        }
        img.src = background;
    }
    useEffect(()=>{
        DrawImage( img_ctx );
    }, [background]);
    
    useEffect(()=>{
        if (!draw_ctx || !img_ctx) {
            //console.log(`Trying to draw to empty canvas- draw ctx:${draw_ctx} image ctx:${img_ctx}`)
            return;
        };        
        DrawTxt( draw_ctx );
    }, [content, activeID]);

    useEffect(()=>{
        if (renderAllTrigger){
            DrawTxt( img_ctx, false, false );
            var IMG = bgCanvasRef.current.toDataURL('image/png');
            var link = document.createElement('a');
            link.download = 'meme.png';
            link.href = IMG;
            link.click();
            link.remove();
            DrawImage()
        }
    },[renderAllTrigger])

    function handleZoomPress(e){
        switch( e.target.name ){
            case 'zoomIn': setZoom( Math.min(zoom + zoomFactor, 5) ); break;
            case 'zoomOut': setZoom( Math.max( zoom - zoomFactor), .1 ); break;
        }
    }   

    return (
        <div className='canvas-master-container tv-screened'>            
            <div 
                className='canvas-inner-container' 
                style={{'--zoom':`${zoom}`/* , minWidth: `${Math.max(content.width/2, 1)}px` */}}// This was breaking the width on mobile
                onMouseEnter={()=>setHovered(true)}
                onMouseLeave={()=>setHovered(false)}
            >                
                { background && hovered && <div className='zoom-container'>
                    <button
                        className='zoom-btn'
                        name='zoomIn'
                        onClick={handleZoomPress}
                        style={{ backgroundImage: `url(${Plus})`}}
                    ></button>
                    <span className='zoom-amount' >{zoom.toFixed(1)}x</span>
                    <button
                        className='zoom-btn'
                        name='zoomOut' 
                        onClick={handleZoomPress}
                        style={{ backgroundImage: `url(${Minus})`}}
                    ></button>                    
                </div> }

                {!background && <div className='splash-screen flex-list'>
                    <h2>Thank you for choosing</h2>
                    <img className='logo-splash' src={Logo}></img>
                    <div className='splash-inner flex-row'>
                         <img src={Upload}></img> Please choose an image to continue.
                    </div>
                    <a href="https://chrisjackem.com"><img className='cmj-logo' src={CMJ}></img></a>
                </div>}

                <canvas
                    id='background-canvas'
                    ref={bgCanvasRef}
                    width={content.width}
                    height={content.height}
                ></canvas>

                <canvas
                    id='main-canvas'
                    ref={canvasRef}
                    width={content.width}
                    height={content.height}
                ></canvas>
            </div>
        </div>
    );
}

export default CanvasComponent;